import { useEffect, useState } from 'react';
import {
  DashboardCardType,
  Role,
  isAdmin,
  type ArticleMetric,
  type DashboardCardInput,
  type DashboardChartKind,
  type DashboardChartStyle,
  type DashboardCountOp,
  type DashboardFilterFieldDto,
  type DashboardSubjectsDto,
  type DashboardSubjectType,
  type SistradeStatusDto,
  type SistradeSyncStatusDto,
  type TeamsSyncConfigDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { BilingualLabelInput } from '../components/BilingualLabelInput';
import { Combobox } from '../components/Combobox';
import { useAuth } from '../auth/AuthContext';
import { KPI_TONES, KpiGlyph } from '../components/kpiIcons';
import { FieldsManager } from './FieldsManager';
import { useT, type Translate } from '../lib/i18n';

/** Definições → Campos da ficha. */
export function SettingsFields() {
  const t = useT();
  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('settings.fields.title')}</h2>
          <p className="page-sub">{t('settings.fields.subtitle')}</p>
        </div>
      </div>
      <FieldsManager />
    </section>
  );
}

/** Definições → Integração SISTRADE (estado + sincronização). */
export function SettingsSistrade() {
  const tr = useT();
  const { user } = useAuth();
  const admin = isAdmin(user?.roles ?? []);
  const [status, setStatus] = useState<SistradeStatusDto | null>(null);
  const [job, setJob] = useState<SistradeSyncStatusDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const busy = job?.state === 'running';

  useEffect(() => {
    api
      .sistradeStatus()
      .then(setStatus)
      .catch((err) => setError((err as Error).message));
    if (admin)
      api
        .sistradeSyncStatus()
        .then(setJob)
        .catch(() => undefined);
  }, [admin]);

  // While a sync runs in the background, poll its state every 2s.
  useEffect(() => {
    if (job?.state !== 'running') return;
    const t = setTimeout(() => {
      api
        .sistradeSyncStatus()
        .then(setJob)
        .catch((err) => setError((err as Error).message));
    }, 2000);
    return () => clearTimeout(t);
  }, [job]);

  async function sync() {
    setError(null);
    try {
      setJob(await api.startSistradeSync());
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{tr('settings.sistrade.title')}</h2>
          <p className="page-sub">{tr('settings.sistrade.subtitle')}</p>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}
      <div className="field-grid" style={{ marginTop: 8 }}>
        <div className="field">
          <dt>{tr('settings.sistrade.mode')}</dt>
          <dd>{status ? status.mode.toUpperCase() : '…'}</dd>
        </div>
        <div className="field">
          <dt>{tr('settings.sistrade.configured')}</dt>
          <dd>
            {status ? (
              <span className={`badge ${status.configured ? 'approved' : 'changes'}`}>
                {status.configured ? tr('common.yes') : tr('common.no')}
              </span>
            ) : (
              '…'
            )}
          </dd>
        </div>
        <div className="field">
          <dt>{tr('settings.sistrade.baseUrl')}</dt>
          <dd>{status?.baseUrl || '—'}</dd>
        </div>
      </div>

      {admin ? (
        <div className="actions" style={{ marginTop: 16 }}>
          <h3>{tr('settings.sistrade.pullTitle')}</h3>
          <p className="muted">{tr('settings.sistrade.pullDesc')}</p>
          <button type="button" onClick={() => void sync()} disabled={busy}>
            {busy ? tr('settings.sistrade.syncing') : tr('settings.sistrade.syncCollaborators')}
          </button>
          {busy && (
            <p className="muted" style={{ marginTop: 12 }}>
              {tr('settings.sistrade.running')}
            </p>
          )}
          {job?.state === 'success' && job.result && (
            <div className="alert ok" style={{ marginTop: 12 }}>
              {tr('settings.sistrade.resultBase', {
                total: job.result.total,
                created: job.result.created,
                updated: job.result.updated,
              })}
              {job.result.skipped
                ? tr('settings.sistrade.resultSkipped', { skipped: job.result.skipped })
                : ''}
              {job.result.fields
                ? tr('settings.sistrade.resultFields', { fields: job.result.fields })
                : ''}
              .{job.result.source === 'employees' ? tr('settings.sistrade.resultReduced') : ''}
            </div>
          )}
          {job?.state === 'error' && (
            <div className="alert error" style={{ marginTop: 12 }}>
              {tr('settings.sistrade.syncFailed', {
                error: job.error ?? tr('settings.sistrade.unknownError'),
              })}
            </div>
          )}
        </div>
      ) : (
        <p className="muted" style={{ marginTop: 12 }}>
          {tr('settings.sistrade.adminOnly')}
        </p>
      )}

      {admin && <TeamsSyncSettings onError={setError} />}
    </section>
  );
}

/** dd/mm/yyyy hh:mm from an ISO timestamp. */
function fmtWhen(iso: string | null | undefined, t: Translate): string {
  if (!iso) return t('settings.sistrade.never');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Scheduled teams/lines sync settings (interval) + manual run. */
function TeamsSyncSettings({ onError }: { onError: (e: string | null) => void }) {
  const t = useT();
  const [cfg, setCfg] = useState<TeamsSyncConfigDto | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api
      .teamsSyncConfig()
      .then((c) => {
        setCfg(c);
        setEnabled(c.enabled);
        setHours(c.intervalHours);
      })
      .catch(() => undefined);
  }, []);

  async function save() {
    setBusy(true);
    setSaved(false);
    onError(null);
    try {
      const c = await api.updateTeamsSyncConfig({ enabled, intervalHours: hours });
      setCfg(c);
      setSaved(true);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function runNow() {
    setBusy(true);
    onError(null);
    try {
      const res = await api.syncTeams();
      if (res.error) onError(t('settings.sistrade.teamsSyncFailed', { error: res.error }));
      setCfg(await api.teamsSyncConfig());
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="actions" style={{ marginTop: 24 }}>
      <h3>{t('settings.sistrade.teamsTitle')}</h3>
      <p className="muted">
        {t('settings.sistrade.teamsDesc')} <strong>{fmtWhen(cfg?.lastSyncAt, t)}</strong>.
      </p>
      <div className="erp-filters" style={{ marginTop: 8 }}>
        <label className="builder-check">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{t('settings.sistrade.autoSync')}</span>
        </label>
        <label className="builder-check">
          <span>{t('settings.sistrade.every')}</span>
          <input
            type="number"
            min={1}
            max={720}
            value={hours}
            onChange={(e) => setHours(Number(e.target.value) || 1)}
            style={{ width: 90 }}
          />
          <span>{t('settings.sistrade.hours')}</span>
        </label>
      </div>
      <div className="actions-row" style={{ marginTop: 12, gap: 10 }}>
        <button type="button" onClick={() => void save()} disabled={busy}>
          {busy ? '…' : t('common.save')}
        </button>
        <button type="button" className="secondary" onClick={() => void runNow()} disabled={busy}>
          {t('settings.sistrade.syncTeamsNow')}
        </button>
        {saved && <span className="page-sub">{t('settings.common.saved')}</span>}
      </div>
    </div>
  );
}

/**
 * Option lists for the Painel builder. Labels are i18n keys resolved via the
 * translator at render time; `group` stays a stable identifier used for
 * grouping (never shown directly — its heading comes from the source labels).
 */
const ROLE_OPTIONS: { value: Role; labelKey: string }[] = [
  { value: Role.Onboarding, labelKey: 'settings.dashboard.role.onboarding' },
  { value: Role.Hr, labelKey: 'settings.dashboard.role.hr' },
  { value: Role.Researcher, labelKey: 'settings.dashboard.role.researcher' },
  { value: Role.Direction, labelKey: 'settings.dashboard.role.direction' },
  { value: Role.Admin, labelKey: 'settings.dashboard.role.admin' },
];

type ChartGroup = 'people' | 'articles';

const CHART_OPTIONS: { value: DashboardChartKind; labelKey: string; group: ChartGroup }[] = [
  { value: 'started', labelKey: 'settings.dashboard.chart.started', group: 'people' },
  { value: 'ended', labelKey: 'settings.dashboard.chart.ended', group: 'people' },
  { value: 'yearCompare', labelKey: 'settings.dashboard.chart.yearCompare', group: 'people' },
  {
    value: 'fieldDistribution',
    labelKey: 'settings.dashboard.chart.fieldDistribution',
    group: 'people',
  },
  { value: 'pubsByYear', labelKey: 'settings.dashboard.chart.pubsByYear', group: 'articles' },
  { value: 'pubsByMonth', labelKey: 'settings.dashboard.chart.pubsByMonth', group: 'articles' },
  {
    value: 'pubsYearCompare',
    labelKey: 'settings.dashboard.chart.pubsYearCompare',
    group: 'articles',
  },
  { value: 'quartileDist', labelKey: 'settings.dashboard.chart.quartileDist', group: 'articles' },
  { value: 'pubsByTeam', labelKey: 'settings.dashboard.chart.pubsByTeam', group: 'articles' },
  {
    value: 'topResearchers',
    labelKey: 'settings.dashboard.chart.topResearchers',
    group: 'articles',
  },
  {
    value: 'citationsByYear',
    labelKey: 'settings.dashboard.chart.citationsByYear',
    group: 'articles',
  },
];

const ARTICLE_METRIC_OPTIONS: { value: ArticleMetric; labelKey: string }[] = [
  { value: 'count', labelKey: 'settings.dashboard.metric.count' },
  { value: 'citations', labelKey: 'settings.dashboard.metric.citations' },
  { value: 'citationsPerPub', labelKey: 'settings.dashboard.metric.citationsPerPub' },
  { value: 'percentQ1', labelKey: 'settings.dashboard.metric.percentQ1' },
  { value: 'percentQ1Q2', labelKey: 'settings.dashboard.metric.percentQ1Q2' },
  { value: 'percentOpenAccess', labelKey: 'settings.dashboard.metric.percentOpenAccess' },
  { value: 'researchers', labelKey: 'settings.dashboard.metric.researchers' },
  { value: 'pubsPerResearcher', labelKey: 'settings.dashboard.metric.pubsPerResearcher' },
  { value: 'pubsThisYear', labelKey: 'settings.dashboard.metric.pubsThisYear' },
  { value: 'pubsGrowthRate', labelKey: 'settings.dashboard.metric.pubsGrowthRate' },
];

const SUBJECT_TYPES: { value: DashboardSubjectType; labelKey: string }[] = [
  { value: 'role', labelKey: 'settings.dashboard.subject.role' },
  { value: 'group', labelKey: 'settings.dashboard.subject.group' },
  { value: 'person', labelKey: 'settings.dashboard.subject.person' },
];

/** Definições → Painel: build the cards each role sees on the dashboard. */
export function SettingsDashboard() {
  const t = useT();
  const [subjectType, setSubjectType] = useState<DashboardSubjectType>('role');
  const [subjectId, setSubjectId] = useState<string>(Role.Hr);
  const [subjects, setSubjects] = useState<DashboardSubjectsDto | null>(null);
  const [fields, setFields] = useState<DashboardFilterFieldDto[]>([]);
  const [cards, setCards] = useState<DashboardCardInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [sectionLabel, setSectionLabel] = useState('');
  const [sectionLabelEn, setSectionLabelEn] = useState('');
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sectionSaved, setSectionSaved] = useState(false);

  useEffect(() => {
    api
      .dashboardFilterFields()
      .then(setFields)
      .catch((err) => setError((err as Error).message));
    api
      .dashboardSubjects()
      .then(setSubjects)
      .catch((err) => setError((err as Error).message));
    api
      .institutionSettings()
      .then((s) => {
        setSectionLabel(s.dashboardSectionLabel ?? '');
        setSectionLabelEn(s.dashboardSectionLabelEn ?? '');
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  async function saveSection() {
    setSectionSaving(true);
    setSectionSaved(false);
    setError(null);
    try {
      await api.updateInstitution({
        dashboardSectionLabel: sectionLabel.trim() ? sectionLabel : null,
        dashboardSectionLabelEn: sectionLabelEn.trim() ? sectionLabelEn : null,
      });
      setSectionSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSectionSaving(false);
    }
  }

  // When switching subject type, default to its first available target.
  function pickType(type: DashboardSubjectType) {
    setSubjectType(type);
    if (type === 'role') setSubjectId(Role.Hr);
    else if (type === 'group') setSubjectId(subjects?.groups[0]?.id ?? '');
    else setSubjectId('');
  }

  useEffect(() => {
    if (!subjectId) {
      setCards([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSaved(false);
    api
      .dashboardConfig(subjectType, subjectId)
      .then((rows) =>
        setCards(
          rows.map((c) => ({
            type: c.type,
            label: c.label,
            labelEn: c.labelEn ?? null,
            tone: c.tone ?? null,
            icon: c.icon ?? null,
            count: c.count ?? null,
            article: c.article ?? null,
            chart: c.chart ?? null,
          })),
        ),
      )
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [subjectType, subjectId]);

  function update(i: number, patch: Partial<DashboardCardInput>) {
    setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
    setSaved(false);
  }
  function move(i: number, dir: -1 | 1) {
    setCards((cs) => {
      const next = [...cs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return cs;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setSaved(false);
  }
  function remove(i: number) {
    setCards((cs) => cs.filter((_, idx) => idx !== i));
    setSaved(false);
  }
  function addCount() {
    setCards((cs) => [
      ...cs,
      {
        type: DashboardCardType.Count,
        label: t('settings.dashboard.newCount'),
        labelEn: null,
        tone: 'kpi-active',
        icon: 'users',
        count: { field: fields[1]?.key ?? 'status', value: '', op: 'eq', activeOnly: true },
      },
    ]);
    setSaved(false);
  }
  function addChart() {
    setCards((cs) => [
      ...cs,
      {
        type: DashboardCardType.Chart,
        label: t('settings.dashboard.chart.started'),
        labelEn: null,
        chart: { chart: 'started' },
      },
    ]);
    setSaved(false);
  }

  async function save() {
    if (!subjectId) {
      setError(t('settings.dashboard.pickSubjectError'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.saveDashboardConfig({
        subjectType,
        subjectId,
        cards: cards.map((c) => ({ ...c, labelEn: c.labelEn ?? null })),
      });
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('settings.dashboard.title')}</h2>
          <p className="page-sub">{t('settings.dashboard.subtitle')}</p>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="builder-card" style={{ marginBottom: 16 }}>
        <label className="builder-field builder-wide">
          <span>{t('settings.dashboard.sectionTitleLabel')}</span>
          <BilingualLabelInput
            labelPt={sectionLabel}
            labelEn={sectionLabelEn}
            ptPlaceholder={t('shared.customCardsHeading')}
            onChange={(pt, en) => {
              setSectionLabel(pt);
              setSectionLabelEn(en);
              setSectionSaved(false);
            }}
          />
        </label>
        <div className="actions-row" style={{ marginTop: 12, gap: 10 }}>
          <button type="button" onClick={() => void saveSection()} disabled={sectionSaving}>
            {sectionSaving ? t('settings.common.saving') : t('common.save')}
          </button>
          {sectionSaved && <span className="page-sub">{t('settings.common.saved')}</span>}
        </div>
      </div>

      <div className="builder-roles" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div className="erp-seg">
          {SUBJECT_TYPES.map((s) => (
            <button
              key={s.value}
              type="button"
              className={subjectType === s.value ? 'on' : ''}
              onClick={() => pickType(s.value)}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>
        {subjectType === 'role' && (
          <div className="erp-seg">
            {ROLE_OPTIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                className={subjectId === r.value ? 'on' : ''}
                onClick={() => setSubjectId(r.value)}
              >
                {t(r.labelKey)}
              </button>
            ))}
          </div>
        )}
        {subjectType === 'group' && (
          <select
            className="erp-select"
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            style={{ minWidth: 220 }}
          >
            {(subjects?.groups ?? []).length === 0 && (
              <option value="">{t('settings.dashboard.noGroups')}</option>
            )}
            {(subjects?.groups ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        )}
        {subjectType === 'person' && (
          <div style={{ minWidth: 240 }}>
            <Combobox
              value={subjectId}
              options={(subjects?.people ?? []).map((p) => ({ value: p.id, label: p.label }))}
              onChange={setSubjectId}
              placeholder={t('settings.dashboard.pickPerson')}
            />
          </div>
        )}
      </div>

      {loading ? (
        <p className="muted">{t('common.loading')}</p>
      ) : (
        <>
          {cards.length === 0 && (
            <div className="panel-empty" style={{ marginBottom: 16 }}>
              {subjectId
                ? t('settings.dashboard.emptyWithSubject')
                : t('settings.dashboard.emptyNoSubject')}
            </div>
          )}

          <div className="builder-list">
            {cards.map((c, i) => (
              <div className="builder-card" key={i}>
                <div className="builder-card-head">
                  <span className="builder-badge">
                    {c.type === DashboardCardType.Count
                      ? t('settings.dashboard.badgeCount')
                      : t('settings.dashboard.badgeChart')}
                  </span>
                  <div className="builder-tools">
                    <button
                      type="button"
                      className="erp-icon-btn"
                      onClick={() => move(i, -1)}
                      title={t('settings.common.moveUp')}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="erp-icon-btn"
                      onClick={() => move(i, 1)}
                      title={t('settings.common.moveDown')}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="erp-icon-btn"
                      onClick={() => remove(i)}
                      title={t('common.remove')}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="builder-grid">
                  <label className="builder-field builder-wide">
                    <span>{t('settings.dashboard.cardTitle')}</span>
                    <BilingualLabelInput
                      labelPt={c.label}
                      labelEn={c.labelEn}
                      onChange={(pt, en) => update(i, { label: pt, labelEn: en })}
                    />
                  </label>

                  {c.type === DashboardCardType.Count ? (
                    <CountEditor card={c} fields={fields} onChange={(patch) => update(i, patch)} />
                  ) : (
                    <ChartEditor card={c} fields={fields} onChange={(patch) => update(i, patch)} />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="builder-actions">
            <button type="button" className="secondary" onClick={addCount}>
              {t('settings.dashboard.addCount')}
            </button>
            <button type="button" className="secondary" onClick={addChart}>
              {t('settings.dashboard.addChart')}
            </button>
            <div className="spacer" />
            {saved && <span className="page-sub">{t('settings.common.saved')}</span>}
            <button type="button" onClick={() => void save()} disabled={saving}>
              {saving ? t('settings.common.saving') : t('common.save')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/** The count-specific controls (field, value, match mode, tone, icon). */
function CountEditor({
  card,
  fields,
  onChange,
}: {
  card: DashboardCardInput;
  fields: DashboardFilterFieldDto[];
  onChange: (patch: Partial<DashboardCardInput>) => void;
}) {
  const t = useT();
  const isArticle = !!card.article;
  const count = card.count ?? { field: 'status', value: '', op: 'eq', activeOnly: true };
  const field = fields.find((f) => f.key === count.field);
  const listId = `vals-${count.field}`;
  function set(patch: Partial<NonNullable<DashboardCardInput['count']>>) {
    onChange({ count: { ...count, ...patch }, article: null });
  }
  return (
    <>
      <label className="builder-field">
        <span>{t('settings.dashboard.source.label')}</span>
        <select
          className="erp-select"
          value={isArticle ? 'articles' : 'people'}
          onChange={(e) =>
            e.target.value === 'articles'
              ? onChange({ article: { metric: card.article?.metric ?? 'count' }, count: null })
              : onChange({ count, article: null })
          }
        >
          <option value="people">{t('settings.dashboard.source.people')}</option>
          <option value="articles">{t('settings.dashboard.source.articles')}</option>
        </select>
      </label>

      {isArticle ? (
        <label className="builder-field">
          <span>{t('settings.dashboard.metricLabel')}</span>
          <select
            className="erp-select"
            value={card.article?.metric ?? 'count'}
            onChange={(e) => onChange({ article: { metric: e.target.value as ArticleMetric } })}
          >
            {ARTICLE_METRIC_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {t(m.labelKey)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <>
          <label className="builder-field">
            <span>{t('settings.dashboard.field')}</span>
            <select
              className="erp-select"
              value={count.field}
              onChange={(e) => set({ field: e.target.value, value: '' })}
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="builder-field">
            <span>{t('settings.dashboard.value')}</span>
            <input
              list={listId}
              value={count.value}
              placeholder={t('settings.dashboard.valuePlaceholder')}
              onChange={(e) => set({ value: e.target.value })}
            />
            <datalist id={listId}>
              {(field?.values ?? []).map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </label>
          <label className="builder-field">
            <span>{t('settings.dashboard.match')}</span>
            <select
              className="erp-select"
              value={count.op ?? 'eq'}
              onChange={(e) => set({ op: e.target.value as DashboardCountOp })}
            >
              <option value="eq">{t('settings.dashboard.matchEq')}</option>
              <option value="ne">{t('settings.dashboard.matchNe')}</option>
              <option value="contains">{t('settings.dashboard.matchContains')}</option>
            </select>
          </label>
        </>
      )}
      <label className="builder-field">
        <span>{t('settings.dashboard.appearance')}</span>
        <div className="builder-aspect">
          <select
            className="erp-select"
            value={card.tone ?? 'kpi-active'}
            onChange={(e) => onChange({ tone: e.target.value })}
          >
            {KPI_TONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <span
            className={`stat-ic ${card.tone ?? 'kpi-active'}`}
            style={{ width: 34, height: 34 }}
          >
            <KpiGlyph name={card.icon} />
          </span>
        </div>
      </label>
      <label className="builder-field">
        <span>{t('settings.dashboard.icon')}</span>
        <select
          className="erp-select"
          value={card.icon ?? 'users'}
          onChange={(e) => onChange({ icon: e.target.value })}
        >
          {['users', 'cap', 'briefcase', 'clock', 'flag', 'building', 'beaker', 'chart'].map(
            (n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ),
          )}
        </select>
      </label>
      {!isArticle && (
        <label className="builder-check">
          <input
            type="checkbox"
            checked={count.activeOnly !== false}
            onChange={(e) => set({ activeOnly: e.target.checked })}
          />
          <span>{t('settings.dashboard.activeOnly')}</span>
        </label>
      )}
    </>
  );
}

/** The chart-specific controls (kind, and the field + style of a distribution). */
function ChartEditor({
  card,
  fields,
  onChange,
}: {
  card: DashboardCardInput;
  fields: DashboardFilterFieldDto[];
  onChange: (patch: Partial<DashboardCardInput>) => void;
}) {
  const t = useT();
  const chart = card.chart ?? { chart: 'started' as DashboardChartKind };
  const isDist = chart.chart === 'fieldDistribution';
  const defaultField = fields.find((f) => f.key !== 'status')?.key ?? 'status';
  function setChart(patch: Partial<NonNullable<DashboardCardInput['chart']>>) {
    onChange({ chart: { ...chart, ...patch } });
  }
  return (
    <>
      <label className="builder-field builder-wide">
        <span>{t('settings.dashboard.chartLabel')}</span>
        <select
          className="erp-select"
          value={chart.chart}
          onChange={(e) => {
            const kind = e.target.value as DashboardChartKind;
            if (kind === 'fieldDistribution') {
              onChange({
                chart: {
                  chart: kind,
                  field: chart.field ?? defaultField,
                  style: chart.style ?? 'bar',
                  activeOnly: chart.activeOnly !== false,
                },
              });
            } else {
              onChange({ chart: { chart: kind } });
            }
          }}
        >
          {(['people', 'articles'] as ChartGroup[]).map((group) => (
            <optgroup key={group} label={t(`settings.dashboard.source.${group}`)}>
              {CHART_OPTIONS.filter((o) => o.group === group).map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      {isDist && (
        <>
          <label className="builder-field">
            <span>{t('settings.dashboard.field')}</span>
            <select
              className="erp-select"
              value={chart.field ?? defaultField}
              onChange={(e) => setChart({ field: e.target.value })}
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="builder-field">
            <span>{t('settings.dashboard.chartStyle')}</span>
            <select
              className="erp-select"
              value={chart.style ?? 'bar'}
              onChange={(e) => setChart({ style: e.target.value as DashboardChartStyle })}
            >
              <option value="bar">{t('settings.dashboard.style.bar')}</option>
              <option value="hbar">{t('settings.dashboard.style.hbar')}</option>
              <option value="donut">{t('settings.dashboard.style.donut')}</option>
            </select>
          </label>
          <label className="builder-check">
            <input
              type="checkbox"
              checked={chart.activeOnly !== false}
              onChange={(e) => setChart({ activeOnly: e.target.checked })}
            />
            <span>{t('settings.dashboard.activeOnly')}</span>
          </label>
        </>
      )}
    </>
  );
}
